import React, { useState, useCallback } from 'react';
import { 
  Key, Copy, Check, RefreshCw, Shield, Eye, EyeOff,
  AlertTriangle, CheckCircle
} from 'lucide-react';

interface PasswordOptions {
  length: number;
  uppercase: boolean;
  lowercase: boolean;
  numbers: boolean;
  symbols: boolean;
  excludeAmbiguous: boolean;
  excludeSimilar: boolean;
}

const PasswordGenerator: React.FC = () => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(true);
  const [copied, setCopied] = useState(false);
  const [options, setOptions] = useState<PasswordOptions>({
    length: 16,
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: true,
    excludeAmbiguous: false,
    excludeSimilar: false,
  });

  const UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const LOWERCASE = 'abcdefghijklmnopqrstuvwxyz';
  const NUMBERS = '0123456789';
  const SYMBOLS = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  const AMBIGUOUS = '{}[]()/\\\'"`~,;:.<>';
  const SIMILAR = 'il1Lo0O';

  const generatePassword = useCallback(() => {
    let charset = '';
    
    if (options.uppercase) charset += UPPERCASE;
    if (options.lowercase) charset += LOWERCASE;
    if (options.numbers) charset += NUMBERS;
    if (options.symbols) charset += SYMBOLS;

    if (options.excludeAmbiguous) {
      charset = charset.split('').filter(c => !AMBIGUOUS.includes(c)).join('');
    }
    if (options.excludeSimilar) {
      charset = charset.split('').filter(c => !SIMILAR.includes(c)).join('');
    }

    if (!charset) {
      setPassword('Select at least one character type');
      return;
    }

    const array = new Uint32Array(options.length);
    crypto.getRandomValues(array);
    
    let result = '';
    for (let i = 0; i < options.length; i++) {
      result += charset[array[i] % charset.length];
    }
    
    setPassword(result);
    setCopied(false);
  }, [options]);

  const copyToClipboard = async () => {
    if (!password || password === 'Select at least one character type') return;
    
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getStrengthScore = (): { score: number; label: string; color: string } => {
    if (!password || password === 'Select at least one character type') {
      return { score: 0, label: 'None', color: 'bg-gray-300' };
    }

    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (password.length >= 16) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^a-zA-Z0-9]/.test(password)) score += 1;

    if (score <= 2) return { score, label: 'Weak', color: 'bg-red-500' };
    if (score <= 4) return { score, label: 'Fair', color: 'bg-yellow-500' };
    if (score <= 5) return { score, label: 'Good', color: 'bg-blue-500' };
    return { score, label: 'Strong', color: 'bg-green-500' };
  };

  const strength = getStrengthScore();

  const hasAtLeastOneType = options.uppercase || options.lowercase || options.numbers || options.symbols;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Password Generator</h2>
        <p className="text-gray-600">
          Generate strong, random passwords for your accounts
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: Options */}
        <div className="space-y-6">
          {/* Length Slider */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm font-medium text-gray-700">
                Password Length
              </label>
              <span className="text-2xl font-bold text-indigo-600">{options.length}</span>
            </div>
            <input
              type="range"
              min="4"
              max="64"
              value={options.length}
              onChange={(e) => setOptions({ ...options, length: parseInt(e.target.value) })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>4</span>
              <span>64</span>
            </div>
          </div>

          {/* Character Types */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Character Types
            </label>
            <div className="space-y-3">
              {[
                { key: 'uppercase', label: 'Uppercase (A-Z)', example: 'ABCDEFGH' },
                { key: 'lowercase', label: 'Lowercase (a-z)', example: 'abcdefgh' },
                { key: 'numbers', label: 'Numbers (0-9)', example: '0123456789' },
                { key: 'symbols', label: 'Symbols (!@#$)', example: '!@#$%^&*' },
              ].map((type) => (
                <label
                  key={type.key}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 cursor-pointer hover:border-indigo-300 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={options[type.key as keyof PasswordOptions] as boolean}
                      onChange={(e) => setOptions({ ...options, [type.key]: e.target.checked })}
                      className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <span className="text-gray-700">{type.label}</span>
                  </div>
                  <span className="text-xs text-gray-400 font-mono">{type.example}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Advanced Options */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Advanced Options
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.excludeAmbiguous}
                  onChange={(e) => setOptions({ ...options, excludeAmbiguous: e.target.checked })}
                  className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <div>
                  <span className="text-gray-700">Exclude ambiguous characters</span>
                  <p className="text-xs text-gray-500">{ } [ ] ( ) / \ ' " ` ~ , ; : . &lt; &gt;</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.excludeSimilar}
                  onChange={(e) => setOptions({ ...options, excludeSimilar: e.target.checked })}
                  className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <div>
                  <span className="text-gray-700">Exclude similar characters</span>
                  <p className="text-xs text-gray-500">i l 1 L o 0 O</p>
                </div>
              </label>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={generatePassword}
            disabled={!hasAtLeastOneType}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-indigo-200"
          >
            <Key className="w-5 h-5" />
            Generate Password
          </button>
        </div>

        {/* Right: Result */}
        <div className="space-y-4">
          {/* Password Display */}
          <div className="bg-gray-900 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400 text-sm">Generated Password</span>
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-4 min-h-[80px] flex items-center justify-center">
              {password ? (
                <p className={`font-mono text-lg break-all ${showPassword ? 'text-green-400' : 'text-gray-500'}`}>
                  {showPassword ? password : '•'.repeat(password.length)}
                </p>
              ) : (
                <p className="text-gray-500 text-center">
                  Click "Generate Password" to create a secure password
                </p>
              )}
            </div>

            {/* Strength Indicator */}
            {password && password !== 'Select at least one character type' && (
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400 text-sm">Strength</span>
                  <span className={`text-sm font-medium ${
                    strength.label === 'Weak' ? 'text-red-400' :
                    strength.label === 'Fair' ? 'text-yellow-400' :
                    strength.label === 'Good' ? 'text-blue-400' : 'text-green-400'
                  }`}>
                    {strength.label}
                  </span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${strength.color} transition-all duration-300`}
                    style={{ width: `${(strength.score / 7) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {password && password !== 'Select at least one character type' && (
            <div className="flex gap-3">
              <button
                onClick={copyToClipboard}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </button>
              
              <button
                onClick={generatePassword}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Security Tips */}
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-indigo-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-indigo-900 mb-2">Security Tips</h4>
                <ul className="text-sm text-indigo-700 space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Use at least 12 characters
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Mix uppercase, lowercase, numbers & symbols
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Use unique passwords for each account
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Consider using a password manager
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Warning for weak settings */}
          {!hasAtLeastOneType && (
            <div className="bg-red-50 rounded-xl p-4 border border-red-200">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <p className="text-red-800 text-sm">
                  Please select at least one character type to generate a password.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordGenerator;
